/** Portal brand derived from customer lead source (TAL Acquisition vs Red Rock). */

import redRockLogo from '../assets/red_rock_logo.jpg';
import redRockFavicon from '../assets/red_rock_favicon.png';
import talLogo from '../assets/tal_logo.png';
import talLogoFull from '../assets/tal_logo_full.png';

export type PortalBrand = 'tal' | 'redrock';

const BRAND_STORAGE_KEY = 'portal_brand';
/** Survives in-tab navigation / logout on localhost so ?brand=tal is restored. */
const ENTRY_BRAND_STORAGE_KEY = 'portal_entry_brand';

// Bundled via webpack so logos always load (public/ static can miss in some setups)
const RED_ROCK_LOGO = redRockLogo;
const TAL_LOGO = talLogoFull; // wordmark for header (more readable at small sizes)
const TAL_LOGO_MARK = talLogo; // circular seal for favicon

const RED_ROCK_FAVICON = redRockFavicon;
const TAL_FAVICON = talLogo;

const RED_ROCK_TITLE = 'Red Rock Consulting & Tax';
const TAL_TITLE = 'The Accounting Lab';

const RED_ROCK_MANIFEST = '/manifest.json';
const TAL_MANIFEST = '/manifest-tal.json';

export interface LeadSourceFields {
  lead_source?: string | null;
  lead_source_id?: string | { id?: string; name?: string; title?: string; lead_source_id?: string } | null;
}

function normalize(value: unknown): string {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
}

/**
 * Extract a comparable string from lead_source_id whether it is plain text
 * or an expanded PocketBase relation record.
 */
function leadSourceIdValue(leadSourceId: LeadSourceFields['lead_source_id']): string {
  if (leadSourceId == null) return '';
  if (typeof leadSourceId === 'string' || typeof leadSourceId === 'number') {
    return normalize(leadSourceId);
  }
  if (typeof leadSourceId === 'object') {
    return normalize(
      leadSourceId.name ??
        leadSourceId.title ??
        leadSourceId.lead_source_id ??
        leadSourceId.id ??
        ''
    );
  }
  return '';
}

/**
 * True when customer is TAL Acquisition.
 * lead_source is case-insensitive: acquisition / Acquisition / ACQUISITION
 * (also accepts common misspelling "Acquistion").
 * lead_source_id: "TAL Acquisition" (string or expanded relation).
 */
export function isTalAcquisitionCustomer(fields?: LeadSourceFields | null): boolean {
  if (!fields) return false;

  const leadSource = normalize(fields.lead_source);
  const leadSourceId = leadSourceIdValue(fields.lead_source_id);

  // normalize() lowercases — Acquisition and acquisition both become "acquisition"
  const isAcquisition =
    leadSource === 'acquisition' ||
    leadSource === 'acquistion' ||
    leadSource.includes('acquisition');
  const isTalAcquisitionId =
    leadSourceId === 'tal acquisition' ||
    leadSourceId === 'tal_acquisition' ||
    leadSourceId.includes('tal acquisition');

  return isAcquisition && isTalAcquisitionId;
}

export function getPortalBrand(fields?: LeadSourceFields | null): PortalBrand {
  return isTalAcquisitionCustomer(fields) ? 'tal' : 'redrock';
}

/**
 * Which legal docs (T&C / consents) to load.
 * - TAL portal host → always TAL docs
 * - Red Rock portal viewing a TAL Acquisition customer → TAL docs
 * - Otherwise → Red Rock docs
 */
export function getLegalDocumentBrand(
  entryBrand: PortalBrand,
  customer?: LeadSourceFields | null
): PortalBrand {
  if (entryBrand === 'tal') return 'tal';
  if (isTalAcquisitionCustomer(customer)) return 'tal';
  return 'redrock';
}

/** Brand from any matching assignment (user-level TAL Acquisition credentials). */
export function getBrandFromAssignments(
  assignments: Array<LeadSourceFields | null | undefined>
): PortalBrand {
  return assignments.some((a) => isTalAcquisitionCustomer(a)) ? 'tal' : 'redrock';
}

export function getBrandLogoSrc(brand: PortalBrand): string {
  return brand === 'tal' ? TAL_LOGO : RED_ROCK_LOGO;
}

/** Circular mark (useful if a compact logo is needed). */
export function getBrandMarkSrc(brand: PortalBrand): string {
  return brand === 'tal' ? TAL_LOGO_MARK : RED_ROCK_LOGO;
}

export function getBrandLogoAlt(brand: PortalBrand): string {
  return brand === 'tal' ? 'The Accounting Lab Logo' : 'Red Rock Tax Logo';
}

export function getBrandDocumentTitle(brand: PortalBrand): string {
  return brand === 'tal' ? TAL_TITLE : RED_ROCK_TITLE;
}

export function getBrandMetaDescription(brand: PortalBrand): string {
  return brand === 'tal'
    ? 'Secure client portal for The Accounting Lab. Access tax returns, documents, invoices, and account updates online.'
    : 'Secure client portal for Red Rock Consulting & Tax. Access tax returns, documents, invoices, and account updates online.';
}

/** Absolute logo URL for Open Graph / link previews. */
export function getBrandOgImageUrl(brand: PortalBrand): string {
  if (typeof window === 'undefined') {
    return brand === 'tal'
      ? 'https://client.theaccountinglab.co/tal_logo.png'
      : 'https://client.redrocktax.com/red_rock_logo.png';
  }
  const path = brand === 'tal' ? '/tal_logo.png' : '/red_rock_logo.png';
  return `${window.location.origin}${path}`;
}

export function getBrandFaviconHref(brand: PortalBrand): string {
  return brand === 'tal' ? TAL_FAVICON : RED_ROCK_FAVICON;
}

export function getBrandManifestHref(brand: PortalBrand): string {
  return brand === 'tal' ? TAL_MANIFEST : RED_ROCK_MANIFEST;
}

function setMetaContent(
  selectorAttr: 'name' | 'property',
  key: string,
  content: string
): void {
  let el = document.querySelector<HTMLMetaElement>(
    `meta[${selectorAttr}="${key}"]`
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(selectorAttr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Update tab title, favicon/apple icons, web manifest, and link-preview meta. */
export function applyPortalBrandToDocument(brand: PortalBrand): void {
  if (typeof document === 'undefined') return;

  const title = getBrandDocumentTitle(brand);
  const description = getBrandMetaDescription(brand);
  const ogImage = getBrandOgImageUrl(brand);
  const pageUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/` : '';

  document.title = title;

  const descEl = document.getElementById('meta-description');
  if (descEl) {
    descEl.setAttribute('content', description);
  } else {
    setMetaContent('name', 'description', description);
  }

  setMetaContent('property', 'og:type', 'website');
  setMetaContent('property', 'og:site_name', title);
  setMetaContent('property', 'og:title', title);
  setMetaContent('property', 'og:description', description);
  setMetaContent('property', 'og:image', ogImage);
  if (pageUrl) setMetaContent('property', 'og:url', pageUrl);

  setMetaContent('name', 'twitter:card', 'summary');
  setMetaContent('name', 'twitter:title', title);
  setMetaContent('name', 'twitter:description', description);
  setMetaContent('name', 'twitter:image', ogImage);

  const theme = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (theme) {
    theme.setAttribute('content', brand === 'tal' ? '#4A6266' : '#1976d2');
  }

  const faviconHref = `${getBrandFaviconHref(brand)}?v=${brand}`;
  const faviconType = 'image/png';

  // Browsers cache favicons aggressively — remove old tags and recreate
  document
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.parentNode?.removeChild(el));

  const addIcon = (rel: string, sizes?: string) => {
    const link = document.createElement('link');
    link.rel = rel;
    link.href = faviconHref;
    link.type = faviconType;
    if (sizes) link.setAttribute('sizes', sizes);
    document.head.appendChild(link);
  };

  addIcon('icon', '32x32');
  addIcon('icon', '16x16');
  addIcon('shortcut icon');
  addIcon('apple-touch-icon', '180x180');

  let manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!manifest) {
    manifest = document.createElement('link');
    manifest.rel = 'manifest';
    document.head.appendChild(manifest);
  }
  manifest.href = getBrandManifestHref(brand);
}

export function persistPortalBrand(brand: PortalBrand): void {
  try {
    localStorage.setItem(BRAND_STORAGE_KEY, brand);
  } catch {
    // ignore storage errors
  }
}

export function readPersistedPortalBrand(): PortalBrand {
  try {
    const stored = localStorage.getItem(BRAND_STORAGE_KEY);
    if (stored === 'tal' || stored === 'redrock') return stored;
  } catch {
    // ignore
  }
  return 'redrock';
}

export function clearPersistedPortalBrand(): void {
  try {
    localStorage.removeItem(BRAND_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Hostnames that always load as TAL (comma-separated in REACT_APP_TAL_HOSTS). */
export function getConfiguredTalHosts(): string[] {
  const raw =
    process.env.REACT_APP_TAL_HOSTS || 'client.theaccountinglab.co';
  return raw
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
}

export function getRedRockPortalUrl(): string {
  return process.env.REACT_APP_REDROCK_PORTAL_URL || 'https://client.redrocktax.com';
}

export function getTalPortalUrl(): string {
  return process.env.REACT_APP_TAL_PORTAL_URL || 'https://client.theaccountinglab.co';
}

export function getOtherPortalUrl(entryBrand: PortalBrand): string {
  return entryBrand === 'tal' ? getRedRockPortalUrl() : getTalPortalUrl();
}

export function persistEntryBrand(brand: PortalBrand): void {
  try {
    sessionStorage.setItem(ENTRY_BRAND_STORAGE_KEY, brand);
  } catch {
    // ignore
  }
}

export function readPersistedEntryBrand(): PortalBrand | null {
  try {
    const stored = sessionStorage.getItem(ENTRY_BRAND_STORAGE_KEY);
    if (stored === 'tal' || stored === 'redrock') return stored;
  } catch {
    // ignore
  }
  return null;
}

/**
 * Brand for this browser entry point.
 * Production: hostname (client.theaccountinglab.co → tal).
 * Localhost: ?brand=tal (persisted in sessionStorage across login/logout).
 */
export function getBrandFromHostname(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): PortalBrand {
  const host = hostname.trim().toLowerCase();

  if (host === 'localhost' || host === '127.0.0.1') {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('brand');
      if (q === 'tal') {
        persistEntryBrand('tal');
        return 'tal';
      }
      if (q === 'redrock') {
        persistEntryBrand('redrock');
        return 'redrock';
      }
    } catch {
      // ignore
    }

    const persisted = readPersistedEntryBrand();
    if (persisted) return persisted;
  }

  const talHosts = getConfiguredTalHosts();
  if (talHosts.some((h) => host === h || host.endsWith(`.${h}`))) {
    persistEntryBrand('tal');
    return 'tal';
  }

  persistEntryBrand('redrock');
  return 'redrock';
}

/** Login path that keeps localhost TAL entry (?brand=tal) after logout. */
export function getLoginPath(
  brand: PortalBrand = getBrandFromHostname()
): string {
  if (typeof window === 'undefined') return '/login';

  const host = window.location.hostname.toLowerCase();
  if (brand === 'tal' && (host === 'localhost' || host === '127.0.0.1')) {
    return '/login?brand=tal';
  }
  return '/login';
}

/** Login / chrome styles for the entry brand. */
export function getBrandLoginStyles(brand: PortalBrand): {
  portalName: string;
  pageBackground: string;
  buttonBackground: string;
  buttonHoverBackground: string;
} {
  if (brand === 'tal') {
    return {
      portalName: TAL_TITLE,
      pageBackground: 'linear-gradient(135deg, #0f3d2e 0%, #1a6b4a 55%, #2d8f65 100%)',
      buttonBackground: 'linear-gradient(45deg, #1a6b4a 30%, #2d8f65 90%)',
      buttonHoverBackground: 'linear-gradient(45deg, #0f3d2e 30%, #1a6b4a 90%)',
    };
  }
  return {
    portalName: RED_ROCK_TITLE,
    pageBackground: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    buttonBackground: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
    buttonHoverBackground: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
  };
}

/**
 * Strict access: on TAL host the user must have ≥1 TAL Acquisition customer;
 * on Red Rock host ≥1 non-TAL customer.
 */
export function assignmentsMatchEntryBrand(
  entryBrand: PortalBrand,
  assignments: Array<LeadSourceFields | null | undefined>
): boolean {
  const list = assignments.filter(Boolean) as LeadSourceFields[];
  if (list.length === 0) return false;

  if (entryBrand === 'tal') {
    return list.some((a) => isTalAcquisitionCustomer(a));
  }
  return list.some((a) => !isTalAcquisitionCustomer(a));
}

/** Keep only customers that belong on this portal domain. */
export function filterAssignmentsForEntryBrand<T extends LeadSourceFields>(
  entryBrand: PortalBrand,
  assignments: T[]
): T[] {
  if (entryBrand === 'tal') {
    return assignments.filter((a) => isTalAcquisitionCustomer(a));
  }
  return assignments.filter((a) => !isTalAcquisitionCustomer(a));
}

/** Same copy as failed password auth — do not reveal portal/brand mismatch. */
export const GENERIC_AUTH_ERROR_MESSAGE =
  'Invalid credentials. Please check your email and password.';

/**
 * Staff (support / admin / all_access) may only use these brands.
 * Default: redrock only. Set REACT_APP_STAFF_ALLOWED_BRANDS=redrock,tal to allow both.
 */
export function getStaffAllowedBrands(): PortalBrand[] {
  const raw = process.env.REACT_APP_STAFF_ALLOWED_BRANDS || 'redrock';
  const brands = raw
    .split(',')
    .map((b) => b.trim().toLowerCase())
    .filter((b): b is PortalBrand => b === 'tal' || b === 'redrock');
  return brands.length > 0 ? brands : ['redrock'];
}

export function isStaffAllowedOnBrand(entryBrand: PortalBrand): boolean {
  return getStaffAllowedBrands().includes(entryBrand);
}

export class BrandMismatchError extends Error {
  readonly entryBrand: PortalBrand;

  constructor(entryBrand: PortalBrand) {
    super(GENERIC_AUTH_ERROR_MESSAGE);
    this.name = 'BrandMismatchError';
    this.entryBrand = entryBrand;
  }
}
